import { FC, useCallback } from 'react';
import { useSelector } from 'react-redux';
import intl from 'react-intl-universal';
import { TableHeadCell, tailwindOverride } from '@getsynapse/design-system';
import { selectSortValue } from 'state/ProjectsList/filters/filtersSlice';
import {
  PROJECTS_TABLE_SORTABLE_COLUMNS,
  ACTIVE_PROJECTS_TABLE_SORTABLE_COLUMNS,
  CONSOLIDATED_PROJECTS_TABLE_SORTABLE_COLUMNS,
} from 'utils/constants/projectsList';

import SelectAllProjects from '../SelectProjectsCheckbox/SelectAllProjects/SelectAllProjects';

interface ProjectsTableHeaderProps {
  handleSort: (column: string) => void;
  isTableEmpty?: boolean;
  isConsolidatedView?: boolean;
}

const ProjectsTableHeader: FC<ProjectsTableHeaderProps> = ({
  handleSort,
  isTableEmpty = false,
  isConsolidatedView = false,
}) => {
  const columns = isConsolidatedView
    ? CONSOLIDATED_PROJECTS_TABLE_SORTABLE_COLUMNS
    : ACTIVE_PROJECTS_TABLE_SORTABLE_COLUMNS;
  const sortValue = useSelector(selectSortValue);

  const onSort = useCallback(
    (orderByParam: string, order: 'desc' | 'asc') => {
      let column = orderByParam;
      if (order === 'desc') {
        column = `-${orderByParam}`;
      }
      handleSort(column);
    },
    [handleSort]
  );

  const stickyHeaderStyles = tailwindOverride(
    'sticky top-0 z-1 bg-primary-lightest h-10'
  );

  const NON_SORTABLE_COLUMNS = [
    PROJECTS_TABLE_SORTABLE_COLUMNS.QUICK_ACTIONS as string,
    PROJECTS_TABLE_SORTABLE_COLUMNS.BUSINESS_UNIT as string,
    PROJECTS_TABLE_SORTABLE_COLUMNS.PROGRAMS as string,
    PROJECTS_TABLE_SORTABLE_COLUMNS.TEAMS as string,
    PROJECTS_TABLE_SORTABLE_COLUMNS.SELECT_ALL as string,
  ] as const;

  const headerCells: Record<
    string,
    {
      columnName: string;
      content: string | React.ReactNode;
      className: string;
    }
  > = {
    [PROJECTS_TABLE_SORTABLE_COLUMNS.SELECT_ALL]: {
      columnName: PROJECTS_TABLE_SORTABLE_COLUMNS.SELECT_ALL,
      content: <SelectAllProjects />,
      className: tailwindOverride({ 'w-12 left-0 z-2': !isTableEmpty }),
    },
    [PROJECTS_TABLE_SORTABLE_COLUMNS.PROJECT_NUMBER]: {
      columnName: PROJECTS_TABLE_SORTABLE_COLUMNS.PROJECT_NUMBER,
      content: intl.get('PROJECTS_LIST_PAGE.TABLE.HEAD.PROJECT_NUMBER'),
      className: tailwindOverride({ 'w-26 left-10 z-2': !isTableEmpty }),
    },
    [PROJECTS_TABLE_SORTABLE_COLUMNS.PROJECT_NAME]: {
      columnName: PROJECTS_TABLE_SORTABLE_COLUMNS.PROJECT_NAME,
      content: intl.get('PROJECTS_LIST_PAGE.TABLE.HEAD.PROJECT_NAME'),
      className: tailwindOverride({ 'w-70 left-42 z-2': !isTableEmpty }),
    },
    [PROJECTS_TABLE_SORTABLE_COLUMNS.STATUS]: {
      columnName: PROJECTS_TABLE_SORTABLE_COLUMNS.STATUS,
      content: intl.get('PROJECTS_LIST_PAGE.TABLE.HEAD.STATUS'),
      className: tailwindOverride({ 'w-32': !isTableEmpty }),
    },
    [PROJECTS_TABLE_SORTABLE_COLUMNS.PRIORITY]: {
      columnName: PROJECTS_TABLE_SORTABLE_COLUMNS.PRIORITY,
      content: intl.get('PROJECTS_LIST_PAGE.TABLE.HEAD.PRIORITY'),
      className: tailwindOverride({ 'w-32': !isTableEmpty }),
    },
    [PROJECTS_TABLE_SORTABLE_COLUMNS.OWNERS]: {
      columnName: PROJECTS_TABLE_SORTABLE_COLUMNS.OWNERS,
      content: intl.get('PROJECTS_LIST_PAGE.TABLE.HEAD.PROJECT_OWNER'),
      className: tailwindOverride({ 'w-44': !isTableEmpty }),
    },
    [PROJECTS_TABLE_SORTABLE_COLUMNS.HEALTH]: {
      columnName: PROJECTS_TABLE_SORTABLE_COLUMNS.HEALTH,
      content: intl.get('PROJECTS_LIST_PAGE.TABLE.HEAD.HEALTH'),
      className: tailwindOverride({ 'w-32': !isTableEmpty }),
    },
    [PROJECTS_TABLE_SORTABLE_COLUMNS.BUSINESS_UNIT]: {
      columnName: PROJECTS_TABLE_SORTABLE_COLUMNS.BUSINESS_UNIT,
      content: intl.get('PROJECTS_LIST_PAGE.TABLE.HEAD.BUSINESS_UNIT'),
      className: tailwindOverride({ 'w-56': !isTableEmpty }),
    },
    [PROJECTS_TABLE_SORTABLE_COLUMNS.PROGRAMS]: {
      columnName: PROJECTS_TABLE_SORTABLE_COLUMNS.PROGRAMS,
      content: intl.get('PROJECTS_LIST_PAGE.TABLE.HEAD.PROGRAMS'),
      className: tailwindOverride({ 'w-36': !isTableEmpty }),
    },
    [PROJECTS_TABLE_SORTABLE_COLUMNS.TEAMS]: {
      columnName: PROJECTS_TABLE_SORTABLE_COLUMNS.TEAMS,
      content: intl.get('PROJECTS_LIST_PAGE.TABLE.HEAD.TEAMS'),
      className: tailwindOverride({ 'w-40': !isTableEmpty }),
    },
    [PROJECTS_TABLE_SORTABLE_COLUMNS.CATEGORY]: {
      columnName: PROJECTS_TABLE_SORTABLE_COLUMNS.CATEGORY,
      content: intl.get('PROJECTS_LIST_PAGE.TABLE.HEAD.PROJECT_CATEGORY'),
      className: tailwindOverride({ 'w-48': !isTableEmpty }),
    },
    [PROJECTS_TABLE_SORTABLE_COLUMNS.START_DATE]: {
      columnName: PROJECTS_TABLE_SORTABLE_COLUMNS.START_DATE,
      content: intl.get('PROJECTS_LIST_PAGE.TABLE.HEAD.START_DATE'),
      className: tailwindOverride({ 'w-32': !isTableEmpty }),
    },
    [PROJECTS_TABLE_SORTABLE_COLUMNS.TARGET_COMPLETION_DATE]: {
      columnName: PROJECTS_TABLE_SORTABLE_COLUMNS.TARGET_COMPLETION_DATE,
      content: intl.get('PROJECTS_LIST_PAGE.TABLE.HEAD.TARGET_COMPLETION_DATE'),
      className: tailwindOverride({ 'w-32': !isTableEmpty }),
    },
    [PROJECTS_TABLE_SORTABLE_COLUMNS.ACTUAL_COMPLETION_DATE]: {
      columnName: PROJECTS_TABLE_SORTABLE_COLUMNS.ACTUAL_COMPLETION_DATE,
      content: intl.get('PROJECTS_LIST_PAGE.TABLE.HEAD.ACTUAL_COMPLETION_DATE'),
      className: tailwindOverride({ 'w-40': !isTableEmpty }),
    },
    [PROJECTS_TABLE_SORTABLE_COLUMNS.RESOURCING_TYPE]: {
      columnName: PROJECTS_TABLE_SORTABLE_COLUMNS.RESOURCING_TYPE,
      content: intl.get('PROJECTS_LIST_PAGE.TABLE.HEAD.RESOURCING_TYPE'),
      className: tailwindOverride({ 'w-36': !isTableEmpty }),
    },
    [PROJECTS_TABLE_SORTABLE_COLUMNS.PROCESS]: {
      columnName: PROJECTS_TABLE_SORTABLE_COLUMNS.PROCESS,
      content: intl.get('PROJECTS_LIST_PAGE.TABLE.HEAD.PROCESS_STAGE'),
      className: tailwindOverride({ 'w-36': !isTableEmpty }),
    },
    [PROJECTS_TABLE_SORTABLE_COLUMNS.STAGE]: {
      columnName: PROJECTS_TABLE_SORTABLE_COLUMNS.STAGE,
      content: intl.get('PROJECTS_LIST_PAGE.TABLE.HEAD.PROJECT_STAGE'),
      className: tailwindOverride({ 'w-36': !isTableEmpty }),
    },
    [PROJECTS_TABLE_SORTABLE_COLUMNS.UPDATED_AT]: {
      columnName: PROJECTS_TABLE_SORTABLE_COLUMNS.UPDATED_AT,
      content: intl.get('PROJECTS_LIST_PAGE.TABLE.HEAD.UPDATED_AT'),
      className: tailwindOverride({ 'w-32': !isTableEmpty }),
    },
    [PROJECTS_TABLE_SORTABLE_COLUMNS.QUICK_ACTIONS]: {
      columnName: PROJECTS_TABLE_SORTABLE_COLUMNS.QUICK_ACTIONS,
      content: '',
      className: tailwindOverride({ 'w-6 right-0 z-2': !isTableEmpty }),
    },
  };

  return (
    <thead
      className={tailwindOverride(
        'h-10 bg-primary-lightest',
        'text-primary text-caption font-semibold'
      )}
    >
      <tr>
        {columns.map((column) => {
          const cell = headerCells[column as keyof typeof headerCells];
          const isActive = sortValue
            ? sortValue === cell.columnName ||
              sortValue === `-${cell.columnName}`
            : false;
          const isDesc = isActive && sortValue === `-${cell.columnName}`;
          const isAsc = isActive && sortValue === cell.columnName;
          return (
            <TableHeadCell
              key={column}
              columnName={
                !NON_SORTABLE_COLUMNS.includes(cell.columnName) &&
                cell.columnName
              }
              content={cell.content}
              className={tailwindOverride(stickyHeaderStyles, {
                [cell.className]: isConsolidatedView,
                'bg-secondary-lightest': isActive,
              })}
              handleSort={() => {
                onSort(cell.columnName, isAsc ? 'desc' : 'asc');
              }}
              isActive={isActive}
              isDesc={isDesc}
              isAsc={isAsc}
              aria-label={cell.columnName}
            />
          );
        })}
      </tr>
    </thead>
  );
};

export default ProjectsTableHeader;
